<!-- src/components/forms/completed/YourDetailsCompleted.vue -->
<template>
  <div v-if="displayData" class="space-y-12 animate-fadeIn text-left">
    
    <!-- 1. IDENTITY SNAPSHOT -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <UserIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Identity Snapshot</h3>
      </div>

      <div class="flex flex-col md:flex-row gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
        <div class="flex flex-col items-center gap-4">
          <div class="relative">
            <img v-if="displayData.photoUrl" :src="displayData.photoUrl" class="h-32 w-32 rounded-full object-cover border-4 border-white shadow-xl" />
            <div v-else class="h-32 w-32 rounded-full bg-slate-200 flex items-center justify-center text-slate-400">
               <UserIcon class="h-12 w-12" />
            </div>
            <div class="absolute -bottom-1 -right-1 bg-[#20FCF1] p-1.5 rounded-full border-2 border-white">
              <CheckBadgeIcon class="h-4 w-4 text-[#0A1630]" />
            </div>
          </div>
          <span class="text-[9px] font-black uppercase text-slate-400 tracking-widest leading-none">Identity Sync: OK</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 flex-1">
          <div class="StatCard bg-white">
            <span class="label">Full Legal Name</span>
            <span class="value uppercase">{{ displayData.firstName }} {{ displayData.lastName }}</span>
          </div>
          <div class="StatCard bg-white">
            <span class="label">Primary Email</span>
            <span class="value lowercase text-xs">{{ displayData.email }}</span>
          </div>
          <div class="StatCard bg-white">
            <span class="label">Contact Number</span>
            <span class="value">{{ displayData.mobilePhoneNumber || 'N/A' }}</span>
          </div>
          <div class="StatCard bg-white">
            <span class="label">Date of Birth</span>
            <span class="value">{{ formatDate(displayData.dateOfBirth, 'dateOnly') }}</span>
          </div>
          <div class="StatCard bg-white md:col-span-2">
            <span class="label">Residential Address</span>
            <span class="value">{{ displayData.addressLine1 || 'N/A' }}, {{ displayData.townOrCity || '' }}, {{ displayData.postCode || '' }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. MEDICAL INDEX -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#0A1630] p-2 rounded-lg shadow-sm">
          <HeartIcon class="h-5 w-5 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Medical Information</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm space-y-6">
          <p class="text-[10px] font-black uppercase text-[#20FCF1] tracking-widest border-b pb-2 leading-none">Practitioner Records</p>
          <div class="flex justify-between items-center"><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">NI Number:</span><span class="text-sm font-black font-mono text-[#0A1630]">{{ displayData.niNumber || 'N/A' }}</span></div>
          <div class="flex justify-between items-center"><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">GP Name:</span><span class="text-sm font-bold text-[#0A1630] uppercase">{{ displayData.gpName || 'N/A' }}</span></div>
          <div><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">Surgery Address:</span><p class="text-sm font-medium text-slate-600 mt-2 leading-relaxed">{{ displayData.gpAddress || 'N/A' }}</p></div>
        </div>

        <div class="p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm space-y-6">
          <p class="text-[10px] font-black uppercase text-[#20FCF1] tracking-widest border-b pb-2 leading-none">Emergency (Next of Kin)</p>
          <div class="flex justify-between items-center"><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">Contact Name:</span><span class="text-sm font-bold text-[#0A1630] uppercase">{{ displayData.nextOfKinName || 'N/A' }}</span></div>
          <div class="flex justify-between items-center"><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">Relationship:</span><span class="text-sm font-bold uppercase text-[#0A1630]">{{ displayData.nextOfKinRelationship || 'N/A' }}</span></div>
          <div class="flex justify-between items-center"><span class="text-xs text-slate-400 font-bold uppercase tracking-tighter">Phone Number:</span><span class="text-sm font-black text-[#0A1630]">{{ displayData.nextOfKinPhoneNumber || 'N/A' }}</span></div>
        </div>
      </div>
    </section>

    <!-- 3. WORK & PROFESSIONAL DOMAIN -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <BriefcaseIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Work & Professional Domain</h3>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">Date Joined</span>
          <span class="value">{{ formatDate(displayData.dateJoinedCompany, 'dateOnly') }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Department</span>
          <span class="value uppercase">{{ formatText(displayData.department) || 'Unassigned' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Job Title</span>
          <span class="value uppercase">{{ formatText(displayData.jobTitle) }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Sentinel ID</span>
          <span class="value tracking-widest">{{ displayData.sentinelNumber || 'N/A' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label text-[#20FCF1]">PTS Expiry</span>
          <span class="value">{{ formatDate(displayData.sentinelExpiryDate, 'dateOnly') }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Sponsorship</span>
          <span class="value uppercase">{{ formatText(displayData.sponsorshipType) || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 4. VEHICLE & FLEET INDEX -->
    <section>
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#0A1630] p-2 rounded-lg shadow-sm text-white">
          <TruckIcon class="h-5 w-5 text-[#EAB308]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">4. Vehicle & Fleet Index</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="StatCard bg-slate-50 border-slate-100">
          <span class="label">Driving Licence No.</span>
          <span class="value font-mono">{{ displayData.drivingLicenceNumber || 'Not Provided' }}</span>
        </div>
        <div class="StatCard bg-slate-50 border-slate-100">
          <span class="label">Licence Expiry</span>
          <span class="value">{{ displayData.drivingLicenceExpiry ? formatDate(displayData.drivingLicenceExpiry, 'dateOnly') : 'N/A' }}</span>
        </div>
        <!-- Admin-Only Vehicle Registration View -->
        <div v-if="userStore.isAdmin || userStore.isHRManager" class="StatCard bg-[#0A1630] border-[#20FCF1]/20 shadow-xl">
          <span class="label text-[#20FCF1]">Assigned Asset (Admin Only)</span>
          <span class="value text-white uppercase">{{ displayData.assignedVehicleReg || 'No Vehicle Linked' }}</span>
        </div>
      </div>
    </section>

    <!-- 5. FINANCIAL AUDIT (Restricted View) -->
    <section v-if="userStore.isAdmin || userStore.isHRManager">
      <div class="flex items-center gap-3 mb-6">
        <div class="bg-[#0A1630] p-2 rounded-lg shadow-sm">
          <BanknotesIcon class="h-5 w-5 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">5. Financial Rate Matrix</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 p-8 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-100">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Basic Rate</span>
            <span class="text-lg font-black text-[#0A1630]">£{{ Number(displayData.basicHourlyRate || 0).toFixed(2) }}</span>
        </div>
        <div class="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-100">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Overtime Hr</span>
            <span class="text-lg font-black text-[#0A1630]">£{{ Number(displayData.overtimeHourlyRate || 0).toFixed(2) }}</span>
        </div>
        <div class="flex justify-between items-center bg-white p-5 rounded-2xl border border-slate-100">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Shift Premium</span>
            <span class="text-lg font-black text-[#0A1630]">£{{ Number(displayData.overtimeShiftRate || 0).toFixed(2) }}</span>
        </div>
      </div>
    </section>

    <!-- 6. AUTHORIZED SIGN-OFF -->
    <section v-if="displayData.signatureDataUrl || displayData.signatureUrl" class="border-t border-slate-100 pt-12">
      <div class="flex flex-col md:flex-row justify-between items-center gap-8">
        <div class="space-y-4 text-center md:text-left max-w-md">
          <p class="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Authorized Identity Seal</p>
          <p class="text-xs text-slate-500 font-medium italic leading-relaxed">"I formally attest that the identity, medical records, and professional details provided herein are accurate and synced with my system profile."</p>
        </div>
        <div class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[280px]">
          <img :src="displayData.signatureDataUrl || displayData.signatureUrl" class="max-h-20 object-contain mb-4 grayscale brightness-75" />
          <div class="w-full h-px bg-slate-100 mb-4"></div>
          <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest leading-none">Profile Registry Synchronised</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { 
  UserIcon, CheckBadgeIcon, 
  HeartIcon, BriefcaseIcon,
  TruckIcon, BanknotesIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();

const props = defineProps({
  submissionData: { type: Object, required: true },
  userProfile: { type: Object, default: null }, 
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true }
});

// prioritize live profile info for admins, fall back to submission snapshot
const displayData = computed(() => {
  return props.userProfile || props.submissionData;
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-5 rounded-[24px] border border-slate-200 shadow-sm transition-all hover:shadow-md; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-[0.15em] mb-2 leading-none; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate leading-none; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>